import { AerDropdownMenu } from "@aerstudios/aerated-aerdropdown";
import { AerInputStructure } from "@aerstudios/aerated-aerinputstructure";
import { ChevronDownIcon } from "@radix-ui/react-icons";
import type { WidgetProps } from "@rjsf/utils";
import cx from "classnames";
import Fuse from "fuse.js";
import type { JSONSchema7 } from "json-schema";
import React, { useRef, useState } from "react";
import { Cross } from "../../../components/common/Icons";
import { Colours } from "../../../utils/colours";
import * as styles from "./MultiSelect.module.scss";

type TriggerProps = {
	id: string;
	items: JSONSchema7[];
	selected: string[];
	variant: "filters" | "default";
	toggleDropdown: () => void;
	setFilteredItems: (items: JSONSchema7[]) => void;
	open: boolean;
	placeholder?: string;
};

const Trigger = React.forwardRef(
	(
		{
			id,
			items,
			selected,
			variant,
			toggleDropdown,
			setFilteredItems,
			open,
			placeholder,
		}: TriggerProps,
		ref:
			| React.ForwardedRef<HTMLInputElement>
			| React.ForwardedRef<HTMLButtonElement>,
	) => {
		if (variant === "filters") {
			const fuse = new Fuse(items, {
				keys: ["title"],
				threshold: 0.2,
			});

			return (
				<div className={styles.selectWrapper}>
					<input
						ref={ref as React.ForwardedRef<HTMLInputElement>}
						id={id}
						placeholder={placeholder}
						type="text"
						className={styles.selectInput}
						onClick={(e) => {
							const { currentTarget } = e;

							if (!open) {
								toggleDropdown();
							}

							// TODO: this is a hack to get the input to focus
							// instead, investigate how to disable radix portal
							// capturing focus
							setTimeout(() => {
								currentTarget.focus();
							}, 0);
						}}
						onChange={(e) => {
							const { currentTarget } = e;
							const results = fuse.search(currentTarget.value);

							const matches =
								currentTarget.value === "" ? items : results.map((r) => r.item);

							setFilteredItems(matches);
						}}
					/>

					<ChevronDownIcon className={styles.icon} aria-hidden="true" />
				</div>
			);
		}

		const buttonText =
			selected.length === 0 ? "Select" : `${selected.length} selected`;

		return (
			<button
				ref={ref as React.ForwardedRef<HTMLButtonElement>}
				type="button"
				onClick={toggleDropdown}
				className={styles.selectButton}
			>
				<div className={styles.buttonContent}>
					<span>{buttonText}</span>

					<ChevronDownIcon aria-hidden="true" />
				</div>
			</button>
		);
	},
);

type FiltersProps = {
	items: string[];
	onRemove: (item: string) => void;
};

const Filters: React.FC<FiltersProps> = ({ items, onRemove }) => {
	// initial value matches $bp-tablet-med in src/theme/variables/_breakpoints.scss
	const [isScrollableContainer, setScrollableContainer] = useState(
		() => window.innerWidth < 750,
	);

	const onToggle = () => setScrollableContainer((v) => !v);

	const buttonText = isScrollableContainer ? "+" : "-";
	const buttonAriaText = isScrollableContainer
		? "Expand filters"
		: "Collapse filters";

	const containerClasses = cx(styles.filters, {
		[styles.scroll]: isScrollableContainer,
		[styles.wrap]: !isScrollableContainer,
	});

	return (
		<>
			<div className={containerClasses}>
				{items.length > 0 && (
					<button
						className={styles.filterViewToggle}
						aria-label={buttonAriaText}
						type="button"
						onClick={onToggle}
					>
						<span aria-hidden="true">{buttonText}</span>
					</button>
				)}

				{items.map((item) => (
					<button
						key={item}
						onClick={() => onRemove(item)}
						className={styles.filter}
						aria-label={`Remove filter ${item}`}
						type="button"
					>
						<span className={styles.filterText}>{item}</span>
						<span className={styles.hiddenCloseMarker} aria-hidden="true">
							<Cross fill={Colours.SUNSHINE_YELLOW} height={"12px"} />
						</span>
					</button>
				))}
			</div>
		</>
	);
};

export type MultiSelectProps = WidgetProps<unknown> & {
	nonSchemaOptions?: { title: string }[];
	variant?: "default" | "filters";
};

const CheckIcon: React.FC = () => null;

export const MultiSelect: React.FC<MultiSelectProps> = ({
	nonSchemaOptions,
	variant = "default",
	value,
	rawErrors: _rawErrors,
	...props
}) => {
	const { anyOf, allOf, oneOf } = props.schema || {};
	const { enumOptions } = props.options;

	const enums = enumOptions?.map((option) => ({ title: option.label }));

	const items =
		nonSchemaOptions || ((anyOf || allOf || oneOf || enums) as JSONSchema7[]);

	// filtering is used with the text search option, however if there is no
	// search available, let this be the canonical list of items
	const [filteredItems, setFilteredItems] = useState(() => items);

	const ref = useRef(null);
	const [open, setOpen] = useState(false);
	const [selected, setSelected] = useState<string[]>(value);

	const closeDropdown = (e: Event) => {
		// ensures that clicking on the trigger won't close the dropdown as you
		// try to open it
		if (ref.current && e.currentTarget === ref.current) {
			return;
		}

		setOpen(false);
	};

	const toggleDropdown = () => setOpen((v) => !v);

	const handleChange = (value: string) => (isChecked: boolean) => {
		setSelected((prevState) => {
			const prevSet = new Set(prevState);

			const verb = isChecked ? "add" : "delete";
			prevSet[verb](value);

			const nextState = [...prevSet];

			// sync checked status to the form
			props.onChange(nextState);

			return nextState;
		});
	};

	return (
		<>
			{variant === "filters" && (
				<Filters
					items={selected}
					onRemove={(item) => handleChange(item)(false)}
				/>
			)}

			<AerInputStructure.Root
				errorMessage={_rawErrors?.[0]}
				onKeyDown={(e) => {
					if (e.key === "Enter") {
						setOpen(true);
					}
				}}
			>
				<AerDropdownMenu.Root
					align="start"
					alignOffset={0}
					sideOffset={0}
					open={open}
					onCloseAutoFocus={closeDropdown}
					onEscapeKeyDown={closeDropdown}
					onPointerDownOutside={closeDropdown}
					onFocusOutside={closeDropdown}
					onInteractOutside={closeDropdown}
					modal={variant === "default"}
					className={styles.dropdown}
					trigger={
						<Trigger
							id={props.id}
							items={items}
							selected={selected}
							variant={variant}
							open={open}
							placeholder={props.options.placeholder}
							toggleDropdown={toggleDropdown}
							setFilteredItems={setFilteredItems}
							ref={ref}
						/>
					}
				>
					{filteredItems.map((answer, i) => (
						<AerDropdownMenu.CheckboxMenuItem
							key={answer.title}
							style={{
								borderTop: i > 0 && "1px solid #222",
							}}
							className={styles.checkboxMenuItem}
							onCheckedChange={handleChange(answer.title)}
							checked={selected.includes(answer.title)}
							checkIcon={<CheckIcon />}
						>
							{answer.title}
						</AerDropdownMenu.CheckboxMenuItem>
					))}

					{filteredItems.length === 0 && (
						<AerDropdownMenu.CheckboxMenuItem disabled>
							No results found
						</AerDropdownMenu.CheckboxMenuItem>
					)}
				</AerDropdownMenu.Root>
				<AerInputStructure.ErrorMessage />
			</AerInputStructure.Root>
		</>
	);
};

export const MultiSelectWithFilters: React.FC<MultiSelectProps> = (props) => (
	<MultiSelect {...props} variant="filters" />
);
